import type { FaqItemData } from '../providers/faqProvider';
import type { HelpDocumentData } from '../providers/helpCenterProvider';
import type { VideoItemData } from '../providers/videosProvider';
import type { ContentAudience } from './audience';
import { richTextToPlainText, toPlainText } from './richText';

// Re-exported so the markdown flattener keeps its long-standing import path.
export { toPlainText };

/**
 * The three content types collapsed into one shape, so the ⌘K palette, the
 * overview hubs and the pin store can treat them uniformly.
 */
export type ResourceEntryKind = 'faq' | 'document' | 'video';

export interface ResourceEntry {
    /** Stable across reloads: what the pin store persists. */
    key: string;
    kind: ResourceEntryKind;
    id: number;
    title: string;
    /** One-line context under the title in lists and search results. */
    subtitle: string;
    categories: string[];
    /** Router path that opens this entry. */
    path: string;
    /** Epoch ms used for "most recent" ordering. */
    timestamp: number;
    /** Lowercased haystack, precomputed once per list rather than per keystroke. */
    haystack: string;
    audience: ContentAudience;
}

export const RESOURCE_KIND_LABEL: Record<ResourceEntryKind, string> = {
    faq: 'FAQ',
    document: 'Help doc',
    video: 'Video'
};

export function entryKey(kind: ResourceEntryKind, id: number): string {
    return `${kind}:${id}`;
}

export function truncate(text: string, max: number): string {
    return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

function timeOf(value: string | undefined): number {
    const parsed = value ? new Date(value).getTime() : NaN;
    return Number.isNaN(parsed) ? 0 : parsed;
}

function makeEntry(
    entry: Omit<ResourceEntry, 'key' | 'haystack'> & { extraSearchText?: string }
): ResourceEntry {
    const { extraSearchText, ...rest } = entry;
    return {
        ...rest,
        key: entryKey(rest.kind, rest.id),
        haystack: [rest.title, rest.subtitle, extraSearchText ?? '', rest.categories.join(' ')]
            .join(' ')
            .toLowerCase()
    };
}

const SUBTITLE_MAX = 160;

export interface ResourceLists {
    faqItems: FaqItemData[];
    helpDocuments: HelpDocumentData[];
    videos: VideoItemData[];
}

/** Builds the flat, searchable index the redesign's shared surfaces read from. */
export function buildResourceIndex(lists: ResourceLists, basePath: string): ResourceEntry[] {
    const faqs = lists.faqItems.map((item) =>
        makeEntry({
            kind: 'faq',
            id: item.id,
            title: item.question,
            subtitle: truncate(richTextToPlainText(item.answer), SUBTITLE_MAX),
            categories: item.category,
            // Answers live on one page; the anchor scrolls to the question.
            path: `${basePath}/faq#faq-${item.id}`,
            timestamp: timeOf(item.updatedAt),
            audience: item.audience,
            extraSearchText: richTextToPlainText(item.answer)
        })
    );

    const documents = lists.helpDocuments.map((doc) =>
        makeEntry({
            kind: 'document',
            id: doc.id,
            title: doc.title,
            subtitle: doc.category.join(' · ') || 'Help doc',
            categories: doc.category,
            path: `${basePath}/help-center/${doc.slug}`,
            timestamp: timeOf(doc.createdAt),
            audience: doc.audience
        })
    );

    const videos = lists.videos.map((video) =>
        makeEntry({
            kind: 'video',
            id: video.id,
            title: video.title,
            subtitle: truncate(richTextToPlainText(video.description), SUBTITLE_MAX),
            categories: video.category,
            path: `${basePath}/videos/${video.slug}`,
            timestamp: timeOf(video.createdAt),
            audience: video.audience
        })
    );

    return [...faqs, ...documents, ...videos];
}

/** Pinned keys resolved against the loaded entries, newest pin first. A pin whose
 * item is no longer visible (deleted, or hidden by audience) is skipped. */
export function selectPinnedEntries(
    pinnedKeys: readonly string[],
    entries: ResourceEntry[]
): ResourceEntry[] {
    const byKey = new Map(entries.map((entry) => [entry.key, entry]));
    return [...pinnedKeys]
        .reverse()
        .map((key) => byKey.get(key))
        .filter((entry): entry is ResourceEntry => entry !== undefined);
}

/** Title prefix beats title substring beats a body hit. Deliberately simple: the
 * catalogue is a few hundred rows in memory, so a real index gains nothing. */
export function searchResources(entries: ResourceEntry[], query: string): ResourceEntry[] {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];

    const scored: Array<{ entry: ResourceEntry; score: number }> = [];
    for (const entry of entries) {
        const title = entry.title.toLowerCase();
        let score = 0;
        if (title.startsWith(needle)) score = 3;
        else if (title.includes(needle)) score = 2;
        else if (entry.haystack.includes(needle)) score = 1;
        if (score > 0) scored.push({ entry, score });
    }

    return scored
        .sort((a, b) => b.score - a.score || b.entry.timestamp - a.entry.timestamp)
        .map(({ entry }) => entry);
}

export function uniqueCategories(entries: Array<{ categories: string[] }>): string[] {
    return Array.from(new Set(entries.flatMap((entry) => entry.categories))).sort((a, b) =>
        a.localeCompare(b)
    );
}

export function formatResourceDate(value: string): string {
    const time = timeOf(value);
    if (!time) return '';
    return new Date(time).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}
